/**
 * Beräkning av intra-hour batteri-arbitrage från kvartsupplösta spotpriser.
 *
 * Sedan 30 september 2025 publicerar Nordpool dagspriser per 15-min intervall.
 * Inom samma timme finns det typiskt prisvariation på 5-30 öre/kWh.
 * Ett batteri som kan reagera per kvart kan utnyttja denna variation:
 * ladda i den billigaste kvarten, ladda ur i den dyraste.
 *
 * Tibber Classic API ger timdata för konsumtion. Vi antar att timmen är
 * jämnt fördelad över kvarterna (det är vad vi tvingas till utan kvartsmätare).
 * Under den antagandet är detta MATEMATISKT EKVIVALENT med en full kvarts-DP:
 * huskonsumtionens bidrag till kostnaden är samma oavsett upplösning, så
 * bara batteriets dispatch drar nytta av kvarts-uppdelning.
 *
 * Volym per arbitrage-par begränsas av min(batteri-effekt × 0.25h, husets
 * konsumtion under kvarten) för att undvika att modellera export under
 * intra-hour-cyklerna (export ger lägre kompensation än import-besparing
 * efter moms och påslag, så det är konservativt att inte exportera).
 *
 * Korrekt vinstformel per par:
 *   vinst = volym × (eff × P_hi - P_lo)
 * där volym = mängden energi vi laddar IN (laddningen kostar P_lo per kWh,
 * och eff × volym kWh kommer UT som ersättning för husinköp vid P_hi).
 */

import type { HourPoint, BatteryConfig } from "./types";

interface IntraHourBonus {
  totalSek: number;
  hoursWithQuarters: number;
}

export function calculateIntraHourArbitrage(
  hours: HourPoint[],
  battery: BatteryConfig,
): IntraHourBonus {
  const maxVolumePerQuarterByPower = battery.maxPowerKW * 0.25;
  const eff = battery.roundTripEfficiency ?? 0.9;

  let totalBonus = 0;
  let hoursWithQuarters = 0;

  for (const h of hours) {
    if (!h.quarterPrices || h.quarterPrices.length < 4) continue;
    hoursWithQuarters++;

    // Husets konsumtion per kvart (antagen jämn)
    const consumptionPerQuarter = h.importKWh / 4;

    // Volymen vi kan rotera i en kvart-cykel utan att exportera = min av:
    // - batteri-effekten under kvarten
    // - husets konsumtion under kvarten (så vi inte måste exportera)
    const volumePerCycle = Math.min(
      maxVolumePerQuarterByPower,
      consumptionPerQuarter,
    );

    if (volumePerCycle <= 0) continue;

    // Sortera kvartpriser stigande
    const sorted = [...h.quarterPrices].sort((a, b) => a - b);
    const lo1 = sorted[0]!;
    const lo2 = sorted[1]!;
    const hi2 = sorted[2]!;
    const hi1 = sorted[3]!;

    // Par 1: lo1 ↔ hi1 (största spread). Vinst = volym × (eff × P_hi - P_lo)
    const profit1 = Math.max(0, volumePerCycle * (eff * hi1 - lo1));

    // Par 2: lo2 ↔ hi2 (näst-största spread)
    const profit2 = Math.max(0, volumePerCycle * (eff * hi2 - lo2));

    totalBonus += profit1 + profit2;
  }

  return { totalSek: totalBonus, hoursWithQuarters };
}
